import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Switch } from '@/components/shadcn/switch'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentApplication } from '../../useCurrentApplication'

type ConsolidatedPermission = RestSchemas['Messages.Applications.ConsolidatedPermission']

type PermissionField = {
  key: string
  label: string
}

const CHANNEL_PERMISSION_FIELDS: PermissionField[] = [
  { key: 'can_create_thread', label: 'Create Thread' },
  { key: 'can_comment', label: 'Comment' },
  { key: 'can_assign_thread', label: 'Assign Thread' },
  { key: 'can_resolve_thread', label: 'Resolve Thread' },
  { key: 'can_delete_message', label: 'Delete Message' },
]

const BASE_ALLOW_FIELDS: PermissionField[] = [
  { key: 'allow_read', label: 'Read' },
  { key: 'allow_upload', label: 'Upload' },
  { key: 'allow_comment', label: 'Comment' },
  { key: 'allow_accept', label: 'Accept' },
  { key: 'allow_discard', label: 'Discard' },
]

const DATA_ASSET_ALLOW_FIELDS: PermissionField[] = [
  { key: 'allow_read', label: 'Read' },
  { key: 'allow_upload', label: 'Upload' },
  { key: 'allow_download_draft', label: 'Download Draft' },
  { key: 'allow_comment', label: 'Comment' },
  { key: 'allow_accept', label: 'Accept' },
  { key: 'allow_discard', label: 'Discard' },
]

const AGENT_PERMISSION_FIELDS: PermissionField[] = [
  { key: 'can_chat', label: 'Chat' },
  { key: 'can_view_history', label: 'View History' },
]

const PIPELINE_PERMISSION_FIELDS: PermissionField[] = [
  { key: 'can_execute', label: 'Execute' },
  { key: 'can_view', label: 'View' },
]

const toCamelCase = (value: string) => value.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
const normalizeValue = (value: string | null | undefined) => (value ?? '').trim().toLowerCase()
const isAgentConfigType = (normalizedConfigType: string) =>
  normalizedConfigType.includes('agent') || normalizedConfigType.includes('sgent')

const getPermissionFieldsByConfigType = (configType: string | null | undefined): PermissionField[] => {
  const normalized = (configType ?? '').toLowerCase()
  if (isAgentConfigType(normalized)) return AGENT_PERMISSION_FIELDS
  if (normalized.includes('pipeline')) return PIPELINE_PERMISSION_FIELDS
  if (normalized.includes('channel')) return CHANNEL_PERMISSION_FIELDS
  if (normalized.includes('asset')) return DATA_ASSET_ALLOW_FIELDS
  return BASE_ALLOW_FIELDS
}

export function PermissionEditPopover({
  item,
  parsedPermissions,
}: {
  item: ConsolidatedPermission
  parsedPermissions: Record<string, unknown>
}) {
  const [open, setOpen] = useState(false)
  const { applicationId } = useCurrentApplication()
  const permissionFields = useMemo(() => getPermissionFieldsByConfigType(item.configType), [item.configType])
  const {
    putApplicationsApiAddDataAssetPermission,
    postApplicationsApiUpdateDataAssetPermission,
    putApplicationsApiAddFileTemplateRole,
    postApplicationsApiUpdateFileTemplateRole,
    getApplicationsApiListApplicationRoles,
    getAgentApiGetAgents,
    getAgentApiGetAgentPermissions,
    putAgentApiAddAgentPermission,
    postAgentApiUpdateAgentPermission,
    deleteAgentApiDeleteAgentPermission,
    putCollaborationApiAddCollaborationPermission,
    postCollaborationApiUpdateCollaborationPermission,
    deleteApplicationsApiDeleteDataAssetPermission,
    deleteApplicationsApiDeleteFileTemplateRole,
    deleteCollaborationApiDeleteCollaborationPermission,
    getApplicationsApiGetConsolidatedPermissions,
    putPipelineApiAddPipelinePermission,
    postPipelineApiUpdatePipelinePermission,
    deletePipelineApiDeletePipelinePermission,
  } = useRestClient()

  const buildDefaultPermissionValues = useCallback(
    () =>
      permissionFields.reduce<Record<string, boolean>>((acc, field) => {
        const snakeCaseValue = parsedPermissions[field.key]
        const camelCaseValue = parsedPermissions[toCamelCase(field.key)]
        acc[field.key] = Boolean(snakeCaseValue ?? camelCaseValue)
        return acc
      }, {}),
    [permissionFields, parsedPermissions]
  )

  const [permissionValues, setPermissionValues] = useState<Record<string, boolean>>(buildDefaultPermissionValues)

  useEffect(() => {
    if (open) {
      setPermissionValues(buildDefaultPermissionValues())
    }
  }, [buildDefaultPermissionValues, open])

  const { mutate: addDataAssetPermission, isPending: isAddingDataAsset } =
    putApplicationsApiAddDataAssetPermission.useMutation()
  const { mutate: updateDataAssetPermission, isPending: isUpdatingDataAsset } =
    postApplicationsApiUpdateDataAssetPermission.useMutation()
  const { mutate: addFileTemplatePermission, isPending: isAddingFileTemplate } =
    putApplicationsApiAddFileTemplateRole.useMutation()
  const { mutate: updateFileTemplatePermission, isPending: isUpdatingFileTemplate } =
    postApplicationsApiUpdateFileTemplateRole.useMutation()
  const { mutate: addAgentPermission, isPending: isAddingAgent } = putAgentApiAddAgentPermission.useMutation()
  const { mutate: updateAgentPermission, isPending: isUpdatingAgent } = postAgentApiUpdateAgentPermission.useMutation()
  const { mutate: deleteAgentPermission, isPending: isDeletingAgent } = deleteAgentApiDeleteAgentPermission.useMutation()
  const { mutate: addChannelPermission, isPending: isAddingChannel } =
    putCollaborationApiAddCollaborationPermission.useMutation()
  const { mutate: updateChannelPermission, isPending: isUpdatingChannel } =
    postCollaborationApiUpdateCollaborationPermission.useMutation()
  const { mutate: deleteDataAssetPermission, isPending: isDeletingDataAsset } =
    deleteApplicationsApiDeleteDataAssetPermission.useMutation()
  const { mutate: deleteFileTemplatePermission, isPending: isDeletingFileTemplate } =
    deleteApplicationsApiDeleteFileTemplateRole.useMutation()
  const { mutate: deleteChannelPermission, isPending: isDeletingChannel } =
    deleteCollaborationApiDeleteCollaborationPermission.useMutation()
  const { mutate: addPipelinePermission, isPending: isAddingPipeline } =
    putPipelineApiAddPipelinePermission.useMutation()
  const { mutate: updatePipelinePermission, isPending: isUpdatingPipeline } =
    postPipelineApiUpdatePipelinePermission.useMutation()
  const { mutate: deletePipelinePermission, isPending: isDeletingPipeline } =
    deletePipelineApiDeletePipelinePermission.useMutation()
  const isSaving =
    isAddingDataAsset ||
    isUpdatingDataAsset ||
    isAddingFileTemplate ||
    isUpdatingFileTemplate ||
    isAddingAgent ||
    isUpdatingAgent ||
    isAddingChannel ||
    isUpdatingChannel ||
    isAddingPipeline ||
    isUpdatingPipeline
  const isDeleting = isDeletingDataAsset || isDeletingFileTemplate || isDeletingAgent || isDeletingChannel || isDeletingPipeline

  const invalidateConsolidatedPermissions = useCallback(
    () =>
      getApplicationsApiGetConsolidatedPermissions.invalidateQueries({
        parameters: {
          query: { applicationId },
        },
      }),
    [applicationId, getApplicationsApiGetConsolidatedPermissions]
  )

  const normalizedConfigType = (item.configType ?? '').toLowerCase()
  const permissionId = Number(
    parsedPermissions.id ??
      parsedPermissions.permissionId ??
      (parsedPermissions as { permission_id?: number }).permission_id ??
      0
  )
  const roleId = Number(
    item.roleId ??
      (item as { role_id?: number }).role_id ??
      (parsedPermissions as { role_id?: number; roleId?: number }).role_id ??
      (parsedPermissions as { role_id?: number; roleId?: number }).roleId ??
      0
  )
  const configId = Number(
    item.configId ??
      (item as { config_id?: number }).config_id ??
      (item as { id?: number }).id ??
      (normalizedConfigType.includes('asset')
        ? (parsedPermissions as { data_asset_id?: number; dataAssetId?: number }).data_asset_id ??
          (parsedPermissions as { data_asset_id?: number; dataAssetId?: number }).dataAssetId
        : normalizedConfigType.includes('channel')
          ? (parsedPermissions as { collaboration_channel_id?: number; collaborationChannelId?: number; channelId?: number })
              .collaboration_channel_id ??
            (parsedPermissions as { collaboration_channel_id?: number; collaborationChannelId?: number; channelId?: number })
              .collaborationChannelId ??
            (parsedPermissions as { collaboration_channel_id?: number; collaborationChannelId?: number; channelId?: number })
              .channelId
          : normalizedConfigType.includes('agent')
            ? (parsedPermissions as { agent_id?: number; agentId?: number }).agent_id ??
              (parsedPermissions as { agent_id?: number; agentId?: number }).agentId
          : (parsedPermissions as { file_template_id?: number; fileTemplateId?: number }).file_template_id ??
            (parsedPermissions as { file_template_id?: number; fileTemplateId?: number }).fileTemplateId) ??
      0
  )
  const agentKey = String(
    (parsedPermissions as { agent_key?: string; agentKey?: string }).agent_key ??
      (parsedPermissions as { agent_key?: string; agentKey?: string }).agentKey ??
      ''
  ).trim()
  const { data: applicationRoles } = getApplicationsApiListApplicationRoles.useQuery(
    { query: { applicationId } },
    {
      enabled: open && (!roleId || isAgentConfigType(normalizedConfigType)),
    }
  )
  const normalizedRoleName = normalizeValue(item.roleName)
  const resolvedRoleId =
    roleId ||
    applicationRoles?.find((role) => normalizeValue(role.name) === normalizedRoleName || normalizeValue(role.description) === normalizedRoleName)
      ?.id ||
    0
  const { data: agents } = getAgentApiGetAgents.useQuery(
    { path: { applicationId } },
    {
      enabled: open && isAgentConfigType(normalizedConfigType) && (!configId || !agentKey),
    }
  )
  const normalizedConfigName = normalizeValue(item.configName)
  const resolvedConfigId = isAgentConfigType(normalizedConfigType)
    ? configId ||
      agents?.find(
        (agent) =>
          normalizeValue(agent.name) === normalizedConfigName ||
          normalizeValue(agent.key) === normalizedConfigName ||
          normalizeValue(agent.description) === normalizedConfigName
      )?.id ||
      0
    : configId
  const resolvedAgentKey = isAgentConfigType(normalizedConfigType)
    ? agentKey ||
      agents?.find(
        (agent) =>
          agent.id === resolvedConfigId ||
          normalizeValue(agent.name) === normalizedConfigName ||
          normalizeValue(agent.key) === normalizedConfigName ||
          normalizeValue(agent.description) === normalizedConfigName
      )?.key ||
      ''
    : ''

  const { data: agentPermissions } = getAgentApiGetAgentPermissions.useQuery(
    { path: { applicationId, agentId: resolvedConfigId } },
    {
      enabled: open && isAgentConfigType(normalizedConfigType) && !!resolvedConfigId,
    }
  )
  const resolvedAgentPermissionId =
    permissionId || agentPermissions?.find((permission) => permission.roleId === resolvedRoleId)?.id || 0

  const handleSave = useCallback(() => {
    if (!resolvedRoleId || !resolvedConfigId) {
      if (isAgentConfigType(normalizedConfigType)) {
        if (!item.configName && !configId) {
          toast.warning('Agent config is missing configId/configName in consolidated data.')
          return
        }
        const missingParts = [
          !resolvedRoleId ? `role (${item.roleName ?? 'unknown'})` : '',
          !resolvedConfigId ? `agent (${item.configName ?? 'unknown'})` : '',
        ]
          .filter(Boolean)
          .join(' and ')
        toast.warning(`Missing ${missingParts} details.`)
      } else {
        toast.warning('Missing role or config details.')
      }
      return
    }

    if (normalizedConfigType.includes('asset')) {
      const dataAssetBody = {
        roleId: resolvedRoleId,
        dataAssetId: resolvedConfigId,
        allowRead: !!permissionValues.allow_read,
        allowUpload: !!permissionValues.allow_upload,
        allowDownloadDraft: !!permissionValues.allow_download_draft,
        allowComment: !!permissionValues.allow_comment,
        allowAccept: !!permissionValues.allow_accept,
        allowDiscard: !!permissionValues.allow_discard,
      }

      updateDataAssetPermission(
        {
          body: dataAssetBody,
        },
        {
          onSuccess: () => {
            toast.success('Updated data asset permission')
            setOpen(false)
            invalidateConsolidatedPermissions()
          },
          onError: () => {
            // If no row exists yet, update can fail; fallback to create.
            addDataAssetPermission(
              { body: dataAssetBody },
              {
                onSuccess: () => {
                  toast.success('Added data asset permission')
                  setOpen(false)
                  invalidateConsolidatedPermissions()
                },
                onError: () => {
                  toast.error('Error saving data asset permission')
                },
              }
            )
          },
        }
      )
      return
    }

    if (normalizedConfigType.includes('channel')) {
      const channelPermissionBody: RestSchemas['Messages.Collaboration.NewCollaborationPermission'] = {
        collaborationChannelId: resolvedConfigId,
        roleId: resolvedRoleId,
        canCreateThread: !!permissionValues.can_create_thread,
        canComment: !!permissionValues.can_comment,
        canAssignThread: !!permissionValues.can_assign_thread,
        canResolveThread: !!permissionValues.can_resolve_thread,
        canDeleteMessage: !!permissionValues.can_delete_message,
      }

      if (!permissionId) {
        addChannelPermission(
          {
            path: {
              applicationId,
              channelId: resolvedConfigId,
            },
            body: channelPermissionBody,
          },
          {
            onSuccess: () => {
              toast.success('Added collaboration channel permission')
              setOpen(false)
              invalidateConsolidatedPermissions()
            },
            onError: () => {
              toast.error('Error adding collaboration channel permission')
            },
          }
        )
        return
      }

      updateChannelPermission(
        {
          path: {
            applicationId,
            channelId: resolvedConfigId,
            permissionId,
          },
          body: channelPermissionBody as RestSchemas['Messages.Collaboration.UpdateCollaborationPermission'],
        },
        {
          onSuccess: () => {
            toast.success('Updated collaboration channel permission')
            setOpen(false)
            invalidateConsolidatedPermissions()
          },
          onError: () => {
            toast.error('Error updating collaboration channel permission')
          },
        }
      )
      return
    }

    if (isAgentConfigType(normalizedConfigType)) {
      if (!resolvedAgentKey) {
        toast.warning('Missing agent key details.')
        return
      }

      const agentPermissionBody: RestSchemas['Messages.Agent.NewAgentPermission'] = {
        agentKey: resolvedAgentKey,
        roleId: resolvedRoleId,
        canChat: !!permissionValues.can_chat,
        canViewHistory: !!permissionValues.can_view_history,
      }

      if (!resolvedAgentPermissionId) {
        addAgentPermission(
          {
            path: {
              applicationId,
              agentId: resolvedConfigId,
            },
            body: agentPermissionBody,
          },
          {
            onSuccess: () => {
              toast.success('Added agent permission')
              setOpen(false)
              invalidateConsolidatedPermissions()
            },
            onError: () => {
              toast.error('Error adding agent permission')
            },
          }
        )
        return
      }

      updateAgentPermission(
        {
          path: {
            applicationId,
            agentId: resolvedConfigId,
            permissionId: resolvedAgentPermissionId,
          },
          body: {
            roleId: resolvedRoleId,
            canChat: !!permissionValues.can_chat,
            canViewHistory: !!permissionValues.can_view_history,
          } as RestSchemas['Messages.Agent.UpdateAgentPermission'],
        },
        {
          onSuccess: () => {
            toast.success('Updated agent permission')
            setOpen(false)
            invalidateConsolidatedPermissions()
          },
          onError: () => {
            toast.error('Error updating agent permission')
          },
        }
      )
      return
    }

    if (normalizedConfigType.includes('pipeline')) {
      const pipelinePermissionBody = {
        pipelineId: resolvedConfigId,
        roleId: resolvedRoleId,
        canExecute: !!permissionValues.can_execute,
        canView: !!permissionValues.can_view,
      }

      if (!permissionId) {
        addPipelinePermission(
          {
            path: { applicationId, pipelineId: resolvedConfigId },
            body: pipelinePermissionBody,
          },
          {
            onSuccess: () => {
              toast.success('Added pipeline permission')
              setOpen(false)
              invalidateConsolidatedPermissions()
            },
            onError: () => {
              toast.error('Error adding pipeline permission')
            },
          }
        )
        return
      }

      updatePipelinePermission(
        {
          path: { applicationId, pipelineId: resolvedConfigId, permissionId },
          body: pipelinePermissionBody,
        },
        {
          onSuccess: () => {
            toast.success('Updated pipeline permission')
            setOpen(false)
            invalidateConsolidatedPermissions()
          },
          onError: () => {
            toast.error('Error updating pipeline permission')
          },
        }
      )
      return
    }

    const fileTemplateBody = {
      roleId: resolvedRoleId,
      fileTemplateId: resolvedConfigId,
      allowRead: !!permissionValues.allow_read,
      allowUpload: !!permissionValues.allow_upload,
      allowComment: !!permissionValues.allow_comment,
      allowAccept: !!permissionValues.allow_accept,
      allowDiscard: !!permissionValues.allow_discard,
    }

    updateFileTemplatePermission(
      {
        body: fileTemplateBody,
      },
      {
        onSuccess: () => {
          toast.success('Updated file template permission')
          setOpen(false)
          invalidateConsolidatedPermissions()
        },
        onError: () => {
          // If no row exists yet, update can fail; fallback to create.
          addFileTemplatePermission(
            { body: fileTemplateBody },
            {
              onSuccess: () => {
                toast.success('Added file template permission')
                setOpen(false)
                invalidateConsolidatedPermissions()
              },
              onError: () => {
                toast.error('Error saving file template permission')
              },
            }
          )
        },
      }
    )
  }, [
    applicationId,
    invalidateConsolidatedPermissions,
    resolvedConfigId,
    resolvedAgentKey,
    normalizedConfigType,
    resolvedAgentPermissionId,
    resolvedRoleId,
    permissionValues.allow_accept,
    permissionValues.allow_comment,
    permissionValues.allow_discard,
    permissionValues.allow_download_draft,
    permissionValues.allow_read,
    permissionValues.allow_upload,
    permissionValues.can_assign_thread,
    permissionValues.can_comment,
    permissionValues.can_create_thread,
    permissionValues.can_delete_message,
    permissionValues.can_resolve_thread,
    permissionValues.can_chat,
    permissionValues.can_view_history,
    addDataAssetPermission,
    addFileTemplatePermission,
    addAgentPermission,
    updateAgentPermission,
    updateChannelPermission,
    updateDataAssetPermission,
    updateFileTemplatePermission,
    addChannelPermission,
    addPipelinePermission,
    updatePipelinePermission,
  ])

  const handleDelete = useCallback(() => {
    if (!resolvedRoleId || !resolvedConfigId) {
      if (isAgentConfigType(normalizedConfigType)) {
        if (!item.configName && !configId) {
          toast.warning('Agent config is missing configId/configName in consolidated data.')
          return
        }
        const missingParts = [
          !resolvedRoleId ? `role (${item.roleName ?? 'unknown'})` : '',
          !resolvedConfigId ? `agent (${item.configName ?? 'unknown'})` : '',
        ]
          .filter(Boolean)
          .join(' and ')
        toast.warning(`Missing ${missingParts} details.`)
      } else {
        toast.warning('Missing role or config details.')
      }
      return
    }

    if (!window.confirm('Are you sure you want to delete this set of permissions?')) {
      return
    }

    if (normalizedConfigType.includes('asset')) {
      deleteDataAssetPermission(
        {
          query: {
            roleId: resolvedRoleId,
            dataAssetId: resolvedConfigId,
          },
        },
        {
          onSuccess: () => {
            toast.success('Deleted data asset permission')
            setOpen(false)
            invalidateConsolidatedPermissions()
          },
          onError: () => {
            toast.error('Error deleting data asset permission')
          },
        }
      )
      return
    }

    if (normalizedConfigType.includes('channel')) {
      if (!permissionId) {
        toast.error('Cannot delete channel permission: missing permission id.')
        return
      }

      deleteChannelPermission(
        {
          path: {
            applicationId,
            channelId: resolvedConfigId,
            permissionId,
          },
        },
        {
          onSuccess: () => {
            toast.success('Deleted collaboration channel permission')
            setOpen(false)
            invalidateConsolidatedPermissions()
          },
          onError: () => {
            toast.error('Error deleting collaboration channel permission')
          },
        }
      )
      return
    }

    if (isAgentConfigType(normalizedConfigType)) {
      if (!resolvedAgentPermissionId) {
        toast.error('Cannot delete agent permission: missing permission id.')
        return
      }

      deleteAgentPermission(
        {
          path: {
            applicationId,
            agentId: resolvedConfigId,
            permissionId: resolvedAgentPermissionId,
          },
        },
        {
          onSuccess: () => {
            toast.success('Deleted agent permission')
            setOpen(false)
            invalidateConsolidatedPermissions()
          },
          onError: () => {
            toast.error('Error deleting agent permission')
          },
        }
      )
      return
    }

    if (normalizedConfigType.includes('pipeline')) {
      if (!permissionId) {
        toast.error('Cannot delete pipeline permission: missing permission id.')
        return
      }

      deletePipelinePermission(
        {
          path: { applicationId, pipelineId: resolvedConfigId, permissionId },
        },
        {
          onSuccess: () => {
            toast.success('Deleted pipeline permission')
            setOpen(false)
            invalidateConsolidatedPermissions()
          },
          onError: () => {
            toast.error('Error deleting pipeline permission')
          },
        }
      )
      return
    }

    deleteFileTemplatePermission(
      {
        query: {
          roleId: resolvedRoleId,
          fileTemplateId: resolvedConfigId,
        },
      },
      {
        onSuccess: () => {
          toast.success('Deleted file template permission')
          setOpen(false)
          invalidateConsolidatedPermissions()
        },
        onError: () => {
          toast.error('Error deleting file template permission')
        },
      }
    )
  }, [
    applicationId,
    resolvedConfigId,
    deleteAgentPermission,
    deleteChannelPermission,
    deleteDataAssetPermission,
    deleteFileTemplatePermission,
    deletePipelinePermission,
    invalidateConsolidatedPermissions,
    normalizedConfigType,
    item.configName,
    item.roleName,
    resolvedAgentPermissionId,
    resolvedRoleId,
  ])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost" type="button">
          <IconEditOutline className="size-4" />
          <span className="sr-only">Edit permissions</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <div className="space-y-4">
          <div className="text-sm font-medium">Edit permissions</div>
          <div className="space-y-2">
            {permissionFields.map((field) => (
              <div key={field.key} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
                <span className="text-sm">{field.label}</span>
                <Switch
                  checked={!!permissionValues[field.key]}
                  onCheckedChange={(checked) =>
                    setPermissionValues((prev) => ({
                      ...prev,
                      [field.key]: !!checked,
                    }))
                  }
                />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button
              size="sm"
              type="button"
              variant="destructive"
              disabled={isSaving || isDeleting}
              onClick={handleDelete}
            >
              <IconDeleteOutline className="mr-1 size-4" />
              Delete
            </Button>
            <Button size="sm" type="button" disabled={isSaving} onClick={handleSave}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
